import axios from "axios";
import { ICabinet, Tags } from "../models/cabinet";
import { IOrganization } from "../models/organization";
import { IStore } from "../models/store";
import { IOrganizationOrderSource } from "../models/organizationOrderSource";
import { IOrderSMSTemplate } from "../models/orderSmsTemplate";
import { IPrinter, PrinterPayload } from "../models/printer";

export const CURRENT_API_VERSION = 3;
export const apiPath = (path: string) => `${process.env.REACT_APP_PUC_CLOUD_URL}/v${process.env.REACT_APP_PUC_API_VERSION}/${path}`;

export const authConfig = (apiKey: string) =>
{
  const config = {
    auth: {
      username: apiKey,
      password: ''
    }
  };
  return config;
};

export const getOrganizations = async (apiKey: string) => {
  return await axios.get<IOrganization[]>(apiPath('organizations'), authConfig(apiKey));
};

export const getOrganization = async (apiKey: string, orgId: string) => {
  const url = `${apiPath('organizations')}/${orgId}`;
  return await axios.get<IOrganization[]>(url, authConfig(apiKey));
};

export const upsertOrganization = async (apiKey: string, data: object) => {
  const url = apiPath('organizations');
  return await axios.post(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((reason) => {
    return reason;
  });
};

export const getStores = async (apiKey: string) => {
  return await axios.get<IStore[]>(apiPath('stores'), authConfig(apiKey));
};

export const getStore = async (apiKey: string, storeId: string) => {
  const url = `${apiPath('stores')}/${storeId}`;
  return await axios.get<IStore>(url, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((reason) => {
    return reason;
  });
};

export const upsertStore = async (apiKey: string, data: object) => {
  const url = `${apiPath('stores')}`;
  return await axios.post(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((reason) => {
    return reason;
  });
};

export const getCabinetsByTag = async (apiKey: string, tag: string) => {
  const url = `${apiPath('cabinets')}?tag=${tag}`;
  return await axios.get<ICabinet[]>(url, authConfig(apiKey));
};

export const getCabinets = async (apiKey: string, orgId: string, storeId: string) => {
  let url = apiPath('cabinet_configs_by_store');
  if (storeId) {
    url = `${url}/?store_id=${storeId}`;
  } else if (orgId) {
    url = `${url}/?organization_id=${orgId}`;
  }
  return await axios.get<ICabinet[]>(url, authConfig(apiKey));
};

export const getCabinetBySerial = async (apiKey: string, serial: string) => {
  const url = `${apiPath('cabinets')}?serial=${serial}`;
  return await axios.get<ICabinet[]>(url, authConfig(apiKey));
};

export const getCabinet = async (apiKey: string, cabinetId: string) => {
  const url = `${apiPath('cabinets')}/${cabinetId}`;
  return await axios.get<ICabinet>(url, authConfig(apiKey));
};

export const getCabinetConfiguration = async (apiKey: string, cabinetId: string) => {
  const url = `${apiPath('cabinets')}/${cabinetId}/configuration`;
  return await axios.get<ICabinet>(url, authConfig(apiKey));
};

export const getCabinetTags = async (apiKey: string, cabinetId: string) => {
  const url = `${apiPath('cabinets')}/${cabinetId}/tags`;
  return await axios.get<Tags>(url, authConfig(apiKey));
};

export const upsertCabinet = async (apiKey: string, data: object) => {
  const url = `${apiPath('cabinets')}`;
  return await axios.post(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((reason) => {
    return reason;
  });
};

export const getKeys = async (apiKey: string) => {
  const url = `${apiPath('keys')}`;
  return await axios.get(url, authConfig(apiKey));
};

export const upsertKey = async (apiKey: string, data: object) => {
  const url = `${apiPath('keys')}`;
  return await axios.post(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};
export const deleteKey = async (apiKey: string, id: number) => {
  const url = `${apiPath('keys')}/${id}`;
  return await axios.delete(url, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const getSmsTemplates = async (apiKey: string, cabinetId: string) => {
  let url = `${apiPath('orders/sms/templates')}`;
  if (cabinetId) {
    url = `${url}/?cabinet-id=${cabinetId}`;
  }
  return await axios.get<Array<IOrderSMSTemplate>>(url, authConfig(apiKey));
};

export const getOrderSourceProperties = async (apiKey: string, orgId: string) => {
  const url = `${apiPath('order_source_properties/?organization_id=')}${orgId}`;
  return await axios.get<IOrganizationOrderSource[]>(url, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const getKnownOrderSources = async (apiKey: string) => {
  const url = `${apiPath('order_sources')}`;

  return await axios.get(url, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const upsertOrderSourceProperties = async (apiKey: string, orgId: string, orderSource: string, data: object) => {
  const url = `${apiPath('order_source_properties/?organization_id=')}${orgId}&order_source_type=${orderSource}`;
  return await axios.post(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const putCabinetDevice = async (apiKey: string, cabinetId: string, cabinetDescription: string, deviceSerial: string) => {
  const url = `${apiPath('cabinets')}/${cabinetId}/device`;
  const data = {
    serial: deviceSerial,
    description: `device for cabinet ${cabinetId} ${cabinetDescription}`
  };
  return await axios.put(url, data, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const getPrinters = async (apiKey: string) => {
  const url = apiPath('printers');
  return await axios.get<IPrinter[]>(url, authConfig(apiKey));
};

export const putPrinter = async (apiKey: string, payload: PrinterPayload) => {
  const url = `${apiPath('printers')}/${payload.serial_number}`;
  return await axios.put(url, payload, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export const deletePrinter = async (apiKey: string, serial: string) => {
  const url = `${apiPath('printers')}/${serial}`;
  return await axios.delete(url, authConfig(apiKey)).then((response) => {
    return response;
  }).catch((err) => {
    return err;
  });
};

export class CabinetFetchDetails {
  public key: string;
  public storeId: string;
  public organizationId: string;
  
  constructor(key: string, organizationId: string, storeId: string) {
    this.key = key;
    this.organizationId = organizationId;
    this.storeId = storeId;
  }
}
